import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  "General Notarization", "Mobile Notary", "Power of Attorney", "Affidavit",
  "Business Document", "Real Estate Document", "Hospital / Nursing Home Visit",
  "Jail Notarization", "Loan Signing", "Other",
];

const TIMES = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM",
  "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM",
];

const field = "w-full bg-transparent border-b border-taupe/50 py-3 text-ink placeholder:text-ink/35 focus:outline-none focus:border-gold transition-colors font-light";

export const Booking = () => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", document_type: "",
    appointment_time: "", meeting_address: "", notes: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.document_type || !date || !form.appointment_time || !form.meeting_address) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/bookings`, {
        ...form,
        appointment_date: format(date, "yyyy-MM-dd"),
      });
      setDone(true);
      toast.success("Booking request sent! Miranda will be in touch shortly.");
      setForm({ full_name: "", phone: "", email: "", document_type: "", appointment_time: "", meeting_address: "", notes: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call (312) 834-7170.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="bg-ivory py-24 lg:py-32" data-testid="booking-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow text-goldDark mb-4">Book Appointment</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
            Request Your <span className="gold-text italic">Notary Visit</span>
          </h2>
          <span className="block w-16 h-px bg-gold mx-auto mt-6" />
          <p className="text-ink/60 font-light mt-5">Fill out the form below and I&apos;ll confirm your appointment promptly.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="relative bg-white rounded-3xl border border-taupe/25 shadow-[0_30px_80px_-40px_rgba(31,31,31,0.4)] p-8 sm:p-12"
        >
          {done ? (
            <div className="text-center py-14" data-testid="booking-success">
              <CheckCircle2 size={56} className="text-gold mx-auto mb-6" strokeWidth={1.2} />
              <h3 className="font-serif text-3xl mb-3">Request Received</h3>
              <p className="text-ink/60 font-light max-w-md mx-auto">Thank you. Miranda will reach out shortly to confirm your appointment details.</p>
              <button onClick={() => setDone(false)} className="mt-8 text-goldDark text-sm tracking-widest uppercase link-underline" data-testid="booking-another-btn">
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="booking-form">
              <div>
                <label className="eyebrow text-taupe text-[10px]">Full Name *</label>
                <input className={field} placeholder="Jane Doe" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-full-name" />
              </div>
              <div>
                <label className="eyebrow text-taupe text-[10px]">Phone Number *</label>
                <input className={field} placeholder="(312) 000-0000" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" />
              </div>
              <div>
                <label className="eyebrow text-taupe text-[10px]">Email Address *</label>
                <input type="email" className={field} placeholder="you@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
              </div>
              <div>
                <label className="eyebrow text-taupe text-[10px]">Document Type *</label>
                <Select value={form.document_type} onValueChange={(v) => set("document_type", v)}>
                  <SelectTrigger className={`${field} rounded-none px-0 h-auto`} data-testid="select-document-type">
                    <SelectValue placeholder="Select a document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => <SelectItem key={d} value={d} data-testid={`doc-${d}`}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="eyebrow text-taupe text-[10px] block">Appointment Date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className={`${field} flex items-center justify-between`} data-testid="input-date">
                      <span className={date ? "text-ink" : "text-ink/35"}>{date ? format(date, "PPP") : "Select a date"}</span>
                      <CalendarIcon size={16} className="text-gold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="eyebrow text-taupe text-[10px]">Appointment Time *</label>
                <Select value={form.appointment_time} onValueChange={(v) => set("appointment_time", v)}>
                  <SelectTrigger className={`${field} rounded-none px-0 h-auto`} data-testid="select-time">
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`time-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow text-taupe text-[10px]">Meeting Address *</label>
                <input className={field} placeholder="Street, City, State, ZIP" value={form.meeting_address} onChange={(e) => set("meeting_address", e.target.value)} data-testid="input-address" />
              </div>
              <div className="sm:col-span-2">
                <label className="eyebrow text-taupe text-[10px]">Additional Notes</label>
                <textarea rows={3} className={`${field} resize-none`} placeholder="Anything I should know before our appointment?" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" />
              </div>
              <div className="sm:col-span-2 pt-2">
                <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-5 text-xs font-medium tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-booking-btn">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Book Appointment <Send size={16} /></>}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Booking;
